import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { HistoryIcon } from '@treams/treams-icons';
import { Avatar, Empty, Label, Paragraph, Skeleton } from '@treams/treams-ui';
import * as graphql from 'generated/graphql';
import moment from 'moment';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import styled from 'styled-components';
import Delta from 'quill-delta';

const Container = styled(Box)`
	border-radius: 8px;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

function GoalAuditRecord(props: { record: graphql.AuditTrail }) {
	const { localize } = useLocalize();

	function serialize(value?: graphql.AuditValue): string | undefined {
		if (!value || !_.isNil(value.booleanValue)) {
			return undefined;
		}

		if (value.dateValue) {
			return moment(value.dateValue).format('L');
		}

		if (value.stringValue) {
			return value.stringValue;
		}

		if (value.localizedStringValue) {
			return localize(value.localizedStringValue)?.text;
		}

		if (value.percentageValue) {
			return (value.percentageValue * 100).toFixed() + '%';
		}

		if (value.quillValue) {
			const quill = value.quillValue as unknown as Delta;
			return quill.ops.map((op) => op.insert ?? '').join('');
		}
	}

	const type = props.record.oldValue
		? props.record.newValue
			? props.record.newValue.booleanValue === true
				? 'on'
				: props.record.newValue.booleanValue === false
				? 'off'
				: 'update'
			: 'remove'
		: 'add';

	const key = [`audit.records.summary.goal`, props.record.field, type].join('.');

	return (
		<Flex my={1}>
			<Box mr={2} flexGrow={0} flexShrink={0}>
				<Avatar user={props.record.createdBy} size={32} />
			</Box>
			<Container flexGrow={1} flexShrink={1} p={3}>
				<Paragraph mb={2} style={{ whiteSpace: 'pre-wrap' }}>
					<Trans
						i18nKey={key}
						values={{
							firstName: props.record.createdBy.firstName,
							oldValue: serialize(props.record.oldValue ?? undefined),
							newValue: serialize(props.record.newValue ?? undefined),
						}}
						components={{
							italic: <i />,
							bold: <strong style={{ color: 'black' }} />,
							strike: <span style={{ textDecoration: 'line-through' }} />,
						}}
					/>
				</Paragraph>
				<Label>{moment(props.record.createdAt).fromNow()}</Label>
			</Container>
		</Flex>
	);
}

GoalAuditRecord.Skeleton = function GoalAuditRecordSkeleton() {
	return (
		<Flex my={1}>
			<Box mr={2} flexGrow={0} flexShrink={0}>
				<Avatar.Skeleton size={32} />
			</Box>
			<Container flexGrow={1} flexShrink={1} p={3}>
				<Paragraph.Skeleton lines={1} mb={2} />
				<Label.Skeleton />
			</Container>
		</Flex>
	);
};

export default function GoalAuditTrail(props: { goalID: string }) {
	const { t } = useTranslation();

	const query = graphql.useGoalAuditTrailQuery({ variables: { goalID: props.goalID } });

	if (!query.data) {
		return (
			<Skeleton.Repeat count={3}>
				<GoalAuditRecord.Skeleton />
			</Skeleton.Repeat>
		);
	}

	if (query.data.goal.auditTrail.edges.length === 0) {
		return (
			<Skeleton.Empty
				contents={
					<Empty
						icon={<HistoryIcon />}
						title={t('goals.show.audit.empty.title')}
						description={t('goals.show.audit.empty.description')}
					/>
				}
			>
				<Skeleton.Repeat count={3}>
					<GoalAuditRecord.Skeleton />
				</Skeleton.Repeat>
			</Skeleton.Empty>
		);
	}

	return (
		<>
			{query.data.goal.auditTrail.edges
				.slice()
				.reverse()
				.map(({ node }) => (
					<GoalAuditRecord key={node.id} record={node as graphql.AuditTrail} />
				))}
		</>
	);
}
